import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, X, Camera } from "lucide-react";
import { PROVINCES } from "@/lib/constants";

export default function ProfileEdit() {
  const [form, setForm] = useState({
    title: "", description: "", age: 25, gender: "female",
    interested_in: "any", city: "", province: "Ontario",
    interests: [], photos: [],
  });
  const [interest, setInterest] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles/me");
        if (data && data.user_id) {
          setForm((f) => ({ ...f, ...data, interests: data.interests || [], photos: data.photos || [] }));
        }
      } catch {}
    })();
  }, []);

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addInterest = () => {
    const t = interest.trim();
    if (!t) return;
    if (form.interests.includes(t)) return;
    setForm((f) => ({ ...f, interests: [...f.interests, t] }));
    setInterest("");
  };

  const removeInterest = (t) =>
    setForm((f) => ({ ...f, interests: f.interests.filter((x) => x !== t) }));

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = (p) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const save = async () => {
    if (!form.title || !form.description || !form.city) {
      toast.error("Please fill in title, description and city.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profiles/me", { ...form, age: Number(form.age) });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-4xl md:text-5xl">Your profile</h1>
        <p className="text-muted-foreground mt-2">The whole point: be yourself, warmly.</p>
      </div>

      <div className="bg-card border border-border rounded-3xl p-8 space-y-8">
        {/* Photos */}
        <div>
          <Label className="text-sm font-semibold">Photos</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Up to 6 photos. The first one is your main photo.</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {form.photos.map((p) => (
              <div key={p} className="relative aspect-square rounded-xl overflow-hidden group">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-photo-${p}`}
                  onClick={() => removePhoto(p)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {form.photos.length < 6 && (
              <label
                data-testid="upload-photo-label"
                className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center cursor-pointer text-muted-foreground"
              >
                {uploading ? (
                  <div className="animate-pulse text-xs">Uploading…</div>
                ) : (
                  <>
                    <Camera className="w-5 h-5 mb-1" />
                    <span className="text-xs">Add photo</span>
                  </>
                )}
                <input
                  type="file" accept="image/*" className="hidden"
                  data-testid="upload-photo-input"
                  onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                />
              </label>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label>Profile title <span className="text-primary">*</span></Label>
            <Input
              data-testid="profile-title-input"
              value={form.title} onChange={(e) => handleChange("title", e.target.value)}
              placeholder="Hiker. Coffee snob. Great with dogs."
              className="mt-1.5 h-11 rounded-xl"
              maxLength={80}
            />
          </div>
          <div>
            <Label>Age</Label>
            <Input
              data-testid="profile-age-input" type="number" min={18} max={99}
              value={form.age} onChange={(e) => handleChange("age", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
        </div>

        <div>
          <Label>About you <span className="text-primary">*</span></Label>
          <Textarea
            data-testid="profile-description-input"
            value={form.description} onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Sunday hikes in the Rockies, terrible at ping pong, better at conversation…"
            className="mt-1.5 min-h-32 rounded-xl"
            maxLength={600}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => handleChange("gender", v)}>
              <SelectTrigger data-testid="profile-gender-select" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Woman</SelectItem>
                <SelectItem value="male">Man</SelectItem>
                <SelectItem value="non-binary">Non-binary</SelectItem>
                <SelectItem value="other">Prefer to self-describe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Interested in</Label>
            <Select value={form.interested_in} onValueChange={(v) => handleChange("interested_in", v)}>
              <SelectTrigger data-testid="profile-interested-select" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Women</SelectItem>
                <SelectItem value="male">Men</SelectItem>
                <SelectItem value="any">Everyone</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label>City <span className="text-primary">*</span></Label>
            <Input
              data-testid="profile-city-input"
              value={form.city} onChange={(e) => handleChange("city", e.target.value)}
              placeholder="Calgary" className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Province / Territory</Label>
            <Select value={form.province} onValueChange={(v) => handleChange("province", v)}>
              <SelectTrigger data-testid="profile-province-select" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Interests</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              data-testid="profile-interest-input"
              value={interest} onChange={(e) => setInterest(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addInterest())}
              placeholder="Hiking, photography, jazz…"
              className="h-11 rounded-xl"
            />
            <Button type="button" onClick={addInterest} data-testid="profile-interest-add" variant="outline" className="rounded-xl h-11">Add</Button>
          </div>
          {form.interests.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {form.interests.map((t) => (
                <Badge key={t} variant="secondary" className="pl-3 pr-1 h-7 rounded-full text-xs">
                  {t}
                  <button onClick={() => removeInterest(t)} className="ml-1 w-5 h-5 rounded-full hover:bg-black/10 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button
            data-testid="profile-save-btn" onClick={save} disabled={saving}
            className="rounded-full h-11 px-8 bg-primary hover:bg-primary/90"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}
